/**
 * Creates a new object with specified keys omitted from the original object.
 *
 * @param object - The source object to omit keys from
 * @param keys - Array of key names to omit from the object
 * @returns A new object with the specified keys removed
 *
 * @example
 * ```typescript
 * const user = { name: 'John', age: 30, email: 'john@example.com' }
 * omit(user, ['email']) // returns { name: 'John', age: 30 }
 * ```
 */
export function omit(object: any, keys: string[]): any {
  const result: Record<string, any> = {}
  Object.keys(object).forEach(key => {
    if (!keys.includes(key)) {
      result[key] = object[key]
    }
  }, {})
  return result
}

/**
 * Creates a new object with only the specified keys picked from the original object.
 *
 * @param object - The source object to pick keys from
 * @param keys - Array of key names to pick from the object
 * @returns A new object containing only the specified keys and their values
 *
 * @example
 * ```typescript
 * const user = { name: 'John', age: 30, email: 'john@example.com' }
 * pick(user, ['name', 'email']) // returns { name: 'John', email: 'john@example.com' }
 * ```
 */
export function pick(object: any, keys: string[]): any {
  const result: Record<string, any> = {}
  keys.forEach(key => {
    // eslint-disable-next-line no-prototype-builtins
    if (object && object.hasOwnProperty(key)) {
      result[key] = object[key]
    }
  })
  return result
}
